import React, { useState, useEffect, useContext, useCallback } from 'react';
import { getAllSmartnodes, getUserSmartnodes, addSmartnodeToUser, removeSmartnodeFromUser } from '../../services/smartnodeService';
import { toast } from 'react-toastify';
import { Button, Card, Container, Row, Col, Collapse, Modal } from 'react-bootstrap';
import { FaFireAlt, FaGift, FaPiggyBank, FaPlusCircle, FaTrashAlt, FaQuestionCircle, FaExclamationTriangle, FaArrowRight, FaCheckCircle, FaTimesCircle, FaWallet, FaCoins, FaChevronDown, FaChevronUp, FaAddressCard, FaClock, FaCube, FaFingerprint, FaPlus, FaTrash } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next'; // Para soporte multiidioma
import './Smartnodes.css';

const SATOSHIS_PER_MENEL = 100000000;

const Smartnodes = () => {
  const { auth } = useContext(AuthContext);
  const { t } = useTranslation();  // Hook para las traducciones
  const [smartnodes, setSmartnodes] = useState([]);
  const [userSmartnodes, setUserSmartnodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [selectedSmartnodeId, setSelectedSmartnodeId] = useState(null);
  const [actionType, setActionType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Obtener todos los smartnodes
  const fetchSmartnodes = useCallback(async () => {
    try {
      const response = await getAllSmartnodes(auth.token);
      const smartnodesData = response.map(node => ({
        ...node,
        balance: node.balance,
        balanceSpendable: node.balanceSpendable,
        received: node.received,
      }));
      setSmartnodes(smartnodesData || []);
    } catch (error) {
      const errorMessage = error.response?.data?.error || t('smartnodes.error_fetch_list');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [auth.token, t]);

  // Obtener los smartnodes del usuario
  const fetchUserSmartnodes = useCallback(async () => {
    try {
      const response = await getUserSmartnodes(auth.token);
      const userSmartnodesData = response.map(node => ({
        ...node,
        balance: node.balance / SATOSHIS_PER_MENEL,
        balanceSpendable: node.balanceSpendable / SATOSHIS_PER_MENEL,
        received: node.received / SATOSHIS_PER_MENEL,
      }));
      setUserSmartnodes(userSmartnodesData || []);
    } catch (error) {
      const errorMessage = error.response?.data?.error || t('smartnodes.error_fetch_user');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [auth.token, t]);

  useEffect(() => {
    if (auth?.token) {
      fetchSmartnodes();
      fetchUserSmartnodes();
    }
  }, [auth?.token, fetchSmartnodes, fetchUserSmartnodes]);

  const toggleExpandCard = (id) => {
    setExpandedCards((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const handleOpenModal = (smartnodeId, type) => {
    setSelectedSmartnodeId(smartnodeId);
    setActionType(type);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSmartnodeId(null);
    setActionType('');
  };

  const handleConfirmAction = async () => {
    setErrorMessage('');
    try {
      let response;
      if (actionType === 'add') {
        response = await addSmartnodeToUser(selectedSmartnodeId, auth.token);
        toast.success(t('smartnodes.add_success'));
      } else if (actionType === 'remove') {
        response = await removeSmartnodeFromUser(selectedSmartnodeId, auth.token);
        toast.success(t('smartnodes.remove_success'));
      }

      if (response && response.data) {
        setTransactionDetails(response.data);
        fetchUserSmartnodes();  // Actualizar los smartnodes del usuario
      } else {
        console.error(t('smartnodes.response_undefined'));
      }

    } catch (error) {
      const backendErrorMessage = error.response?.data?.error || error.message || t('smartnodes.error_process_action');
      setErrorMessage(backendErrorMessage);
    }

    handleCloseModal();
  };

  const totalBalance = userSmartnodes.reduce((acc, node) => acc + (node.balance ?? 0), 0);
  const totalBalanceSpendable = userSmartnodes.reduce((acc, node) => acc + (node.balanceSpendable ?? 0), 0);
  const totalReceived = userSmartnodes.reduce((acc, node) => acc + (node.received ?? 0), 0);

  const totalUserSmartnodes = userSmartnodes.length;
  const totalAllSmartnodes = smartnodes.length;

  if (loading) return <p>{t('smartnodes.loading')}</p>;

  return (
    <Container>
      <h1 className="mb-4">{t('smartnodes.title')} ({totalUserSmartnodes}/{totalAllSmartnodes})</h1>

      <Modal show={showModal} onHide={handleCloseModal} centered className="smartnode-modal">

        <Modal.Header closeButton>
          <Modal.Title className="text-center w-100">
            <FaQuestionCircle className="mr-2" />
            {t(actionType === 'add' ? 'smartnodes.confirm_add' : 'smartnodes.confirm_remove')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {actionType === 'add' ? (
            <>
              <FaPlusCircle className="text-success mr-2" />
              {t('smartnodes.confirm_add')}
            </>
          ) : (
            <>
              <FaTrashAlt className="text-danger mr-2" />
              {t('smartnodes.confirm_remove')}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-around">
          <Button variant="secondary" onClick={handleCloseModal}>
            {t('smartnodes.cancel')}
          </Button>
          <Button variant={actionType === 'add' ? 'success' : 'danger'} onClick={handleConfirmAction}>
            {actionType === 'add' ? (
              <>
                <FaPlusCircle className="mr-2" /> {t('smartnodes.add_smartnode')}
              </>
            ) : (
              <>
                <FaTrashAlt className="mr-2" /> {t('smartnodes.remove_smartnode')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {errorMessage && (
        <Modal show={true} onHide={() => setErrorMessage('')} centered className="smartnode-modal">
          <Modal.Header closeButton>
            <Modal.Title className="text-center w-100">
              <FaExclamationTriangle className="text-danger mr-2" /> {t('smartnodes.error')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <p className="text-danger">
              <FaTimesCircle className="mr-2" /> {errorMessage}
            </p>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <Button variant="danger" onClick={() => setErrorMessage('')}>
              {t('smartnodes.close')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {transactionDetails && (
         <Modal show={true} onHide={() => setTransactionDetails(null)} centered className="smartnode-modal">
          <Modal.Header closeButton>
            <Modal.Title className="text-center w-100">
              <FaCheckCircle className="text-success mr-2" /> {t('smartnodes.transaction_details')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-4">
              <p style={{ wordBreak: 'break-all' }}>
                <strong>TxID:</strong> {transactionDetails.txid}
              </p>
            </div>
            <div className="text-left">
              <h5>{t('smartnodes.involved_addresses')}:</h5>
              {transactionDetails.involvedAddresses.map((address, index) => (
                <p key={index} style={{ wordBreak: 'break-all' }}>
                  {address.alias === 'Game Fund' && <FaPiggyBank className="mr-2 text-warning" />}
                  {address.alias === 'Burn Address' && <FaFireAlt className="mr-2 text-danger" />}
                  {address.alias === 'Rewards Address' && <FaGift className="mr-2 text-success" />}
                  {address.alias === 'Change' && <FaArrowRight className="mr-2 text-info" />}
                  <strong>{address.alias}:</strong> {address.address}
                </p>
              ))}
              <h5 className="mt-4">{t('smartnodes.total_amounts')}:</h5>
              <p><strong>{t('smartnodes.game_fund_amount')}:</strong> <FaPiggyBank className="mr-1 text-warning" /> {transactionDetails.amounts.gameFundAmount}</p>
              <p><strong>{t('smartnodes.burn_amount')}:</strong> <FaFireAlt className="mr-1 text-danger" /> {transactionDetails.amounts.burnAmount}</p>
              <p><strong>{t('smartnodes.rewards_amount')}:</strong> <FaGift className="mr-1 text-success" /> {transactionDetails.amounts.rewardsAmount}</p>
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <Button variant="primary" onClick={() => setTransactionDetails(null)}>
              {t('smartnodes.close')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Tarjetas de Balance */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center shadow-sm">
            <Card.Body>
              <FaCoins size={40} className="mb-3 text-primary" />
              <Card.Title>{t('smartnodes.total_balance')}</Card.Title>
              <Card.Text className="h4">{totalBalance.toFixed(8)} MENEL</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center shadow-sm">
            <Card.Body>
              <FaCoins size={40} className="mb-3 text-success" />
              <Card.Title>{t('smartnodes.total_balance_spendable')}</Card.Title>
              <Card.Text className="h4">{totalBalanceSpendable.toFixed(8)} MENEL</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center shadow-sm">
            <Card.Body>
              <FaCoins size={40} className="mb-3 text-warning" />
              <Card.Title>{t('smartnodes.total_received')}</Card.Title>
              <Card.Text className="h4">{totalReceived.toFixed(8)} MENEL</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Smartnodes del Usuario */}
      <Row>
        <Col>
          <h2 className="mb-4">{t('smartnodes.my_smartnodes')} ({totalUserSmartnodes})</h2>
          <Row>
            {userSmartnodes.map((node) => (
              <Col md={4} key={`${node.id}-${node.proTxHash}`} className="mb-4">
                <Card className="shadow-sm">
                  <Card.Body>
                    <Card.Title className="d-flex align-items-center" style={{ fontSize: '1rem' }}>
                      <FaWallet className="mr-2" />
                      <span style={{ wordBreak: 'break-all' }}> {node.payee}</span>
                    </Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">
                      {t('smartnodes.status')}: {node.status === 'ENABLED' ? <FaCheckCircle color="green" /> : <FaTimesCircle color="red" />} {node.status}
                    </Card.Subtitle>
                    <Card.Text><FaCoins /> {t('smartnodes.balance')}: {(node.balance).toFixed(8)} MENEL</Card.Text>
                    <Card.Text><FaCoins /> {t('smartnodes.balance_spendable')}: {(node.balanceSpendable).toFixed(8)} MENEL</Card.Text>
                    <Card.Text><FaCoins /> {t('smartnodes.total_received')}: {(node.received).toFixed(8)} MENEL</Card.Text>
                    <div className="d-flex justify-content-between">
                      <Button variant="danger" onClick={() => handleOpenModal(node.id, 'remove')}>
                        <FaTrash /> {t('smartnodes.remove')}
                      </Button>
                      <Button variant="link" onClick={() => toggleExpandCard(node.id)}>
                        {expandedCards[node.id] ? <FaChevronUp /> : <FaChevronDown />} {t('smartnodes.more_info')}
                      </Button>
                    </div>
                    <Collapse in={expandedCards[node.id]}>
                      <div className="mt-3">
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.protx_hash')}:</strong> {node.proTxHash}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.address')}:</strong> {node.address}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.payee')}:</strong> {node.payee}</Card.Text>
                        <Card.Text><FaCube /> <strong>{t('smartnodes.last_paid_block')}:</strong> {node.lastpaidblock}</Card.Text>
                        <Card.Text><FaClock /> <strong>{t('smartnodes.last_paid_time')}:</strong> {new Date(node.lastpaidtime * 1000).toLocaleString()}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.owner_address')}:</strong> {node.owneraddress}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.voting_address')}:</strong> {node.votingaddress}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.collateral_address')}:</strong> {node.collateraladdress}</Card.Text>
                        <Card.Text><FaFingerprint /> <strong>{t('smartnodes.pubkey_operator')}:</strong> {node.pubkeyoperator}</Card.Text>
                        <Card.Text><FaExclamationTriangle /> <strong>{t('smartnodes.pose_penalty')}:</strong> {node.posepenalty}</Card.Text>
                      </div>
                    </Collapse>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      {/* Todos los Smartnodes */}
      <Row>
        <Col>
          <h1 className="mb-4">{t('smartnodes.all_smartnodes')} ({totalAllSmartnodes})</h1>
          <Row>
            {smartnodes.map((node) => (
              <Col md={4} key={`${node.id}-${node.proTxHash}`} className="mb-4">
                <Card className="shadow-sm">
                  <Card.Body>
                    <Card.Title className="d-flex align-items-center" style={{ fontSize: '1rem' }}>
                      <FaWallet className="mr-2" />
                      <span style={{ wordBreak: 'break-all' }}> {node.payee}</span>
                    </Card.Title>
                    <Card.Subtitle className="mb-2 text-muted">
                      {t('smartnodes.status')}: {node.status === 'ENABLED' ? <FaCheckCircle color="green" /> : <FaTimesCircle color="red" />} {node.status}
                    </Card.Subtitle>
                    <Card.Text><FaCoins /> {t('smartnodes.balance')}: {(node.balance / SATOSHIS_PER_MENEL).toFixed(8)} MENEL</Card.Text>
                    <Card.Text><FaCoins /> {t('smartnodes.balance_spendable')}: {(node.balanceSpendable / SATOSHIS_PER_MENEL).toFixed(8)} MENEL</Card.Text>
                    <Card.Text><FaCoins /> {t('smartnodes.total_received')}: {(node.received / SATOSHIS_PER_MENEL).toFixed(8)} MENEL</Card.Text>
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="success"
                        onClick={() => handleOpenModal(node.id, 'add')}
                        disabled={userSmartnodes.some((n) => n.id === node.id)}
                      >
                        <FaPlus /> {t('smartnodes.add')}
                      </Button>
                      <Button variant="link" onClick={() => toggleExpandCard(node.id)}>
                        {expandedCards[node.id] ? <FaChevronUp /> : <FaChevronDown />} {t('smartnodes.more_info')}
                      </Button>
                    </div>
                    <Collapse in={expandedCards[node.id]}>
                      <div className="mt-3">
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.protx_hash')}:</strong> {node.proTxHash}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.address')}:</strong> {node.address}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.payee')}:</strong> {node.payee}</Card.Text>
                        <Card.Text><FaCube /> <strong>{t('smartnodes.last_paid_block')}:</strong> {node.lastpaidblock}</Card.Text>
                        <Card.Text><FaClock /> <strong>{t('smartnodes.last_paid_time')}:</strong> {new Date(node.lastpaidtime * 1000).toLocaleString()}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.owner_address')}:</strong> {node.owneraddress}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.voting_address')}:</strong> {node.votingaddress}</Card.Text>
                        <Card.Text><FaAddressCard /> <strong>{t('smartnodes.collateral_address')}:</strong> {node.collateraladdress}</Card.Text>
                        <Card.Text><FaFingerprint /> <strong>{t('smartnodes.pubkey_operator')}:</strong> {node.pubkeyoperator}</Card.Text>
                        <Card.Text><FaExclamationTriangle /> <strong>{t('smartnodes.pose_penalty')}:</strong> {node.posepenalty}</Card.Text>
                      </div>
                    </Collapse>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default Smartnodes;
